import { endOfDay, startOfDay } from 'date-fns';

import type { StageSemaphoreFlagType } from '@/interfaces/deal/semaphoreFlags.interface';
import type { UserPermission } from '@/interfaces/user/permissionFilters.interface';
import type {
  PipelineDealsApiFilter,
  PipelineDealsAppliedFilters,
  PipelineDealsSerializedFilters,
  PipelineDealsTextRange,
  PipelineTimeToBuyFilter,
} from '@/modules/pipeline/types/PipelineFilters.types';
import { toOptionalCurrencyRange } from '@/modules/pipeline/utils/Pipeline.utils';

type SerializeFiltersOptions = {
  canViewAll: boolean;
  defaultMode: UserPermission;
};

const isFilledText = (value?: string): value is string => {
  return value !== undefined && value.trim() !== '';
};

const isValidDate = (value?: Date): value is Date => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

const hasValidArrayItem = <T>(
  values: T[],
  predicate: (value: T) => boolean,
): boolean => {
  return values.some(predicate);
};

const normalizeIds = (values: string[]): string[] => {
  return [...new Set(values.filter(Boolean))].sort((left, right) =>
    left.localeCompare(right),
  );
};

const normalizeTimeToBuy = (
  values: PipelineTimeToBuyFilter[],
): PipelineTimeToBuyFilter[] => {
  return [...new Set(values.filter(isFilledText))].sort((left, right) =>
    left.localeCompare(right),
  ) as PipelineTimeToBuyFilter[];
};

const cloneTextRange = (
  value: PipelineDealsTextRange,
): PipelineDealsTextRange => ({
  ...(value.min !== undefined && { min: value.min }),
  ...(value.max !== undefined && { max: value.max }),
});

const parseOptionalNumber = (value?: string): number | undefined => {
  if (!isFilledText(value)) {
    return undefined;
  }

  const parsed = Number(value.trim());

  return Number.isFinite(parsed) ? parsed : undefined;
};

const toOptionalNumberRange = (
  min?: string,
  max?: string,
): {
  min?: number;
  max?: number;
} | null => {
  const parsedMin = parseOptionalNumber(min);
  const parsedMax = parseOptionalNumber(max);

  if (parsedMin === undefined && parsedMax === undefined) {
    return null;
  }

  return {
    ...(parsedMin !== undefined && { min: parsedMin }),
    ...(parsedMax !== undefined && { max: parsedMax }),
  };
};

const toOptionalIntegerCurrencyRange = (
  min?: string,
  max?: string,
): {
  min?: number;
  max?: number;
} | null => {
  const range = toOptionalCurrencyRange(min, max);

  if (!range) {
    return null;
  }

  return {
    ...(range.min !== undefined && { min: Math.trunc(range.min) }),
    ...(range.max !== undefined && { max: Math.trunc(range.max) }),
  };
};

const hasFilledTextRange = (value: PipelineDealsTextRange): boolean => {
  return isFilledText(value.min) || isFilledText(value.max);
};

const hasFilledDateRange = (
  value: PipelineDealsAppliedFilters['createdAt'],
): boolean => {
  return isValidDate(value.min) || isValidDate(value.max);
};

const hasSelectedBooleanValue = (
  value: PipelineDealsAppliedFilters['isComplete'],
): boolean => {
  return value === 'true' || value === 'false';
};

const hasActiveStageFilter = (
  value: PipelineDealsAppliedFilters['stage'],
): boolean => {
  const hasStageIds = hasValidArrayItem(value.id, isFilledText);
  const hasStageName = isFilledText(value.name);

  const hasUsefulPipelineId =
    isFilledText(value.pipelineId) && (hasStageIds || hasStageName);

  return hasStageIds || hasStageName || hasUsefulPipelineId;
};

const normalizeCreatedAtRange = (
  value: PipelineDealsAppliedFilters['createdAt'],
): PipelineDealsApiFilter['createdAt'] | undefined => {
  const min = value.min ? startOfDay(value.min).toISOString() : undefined;
  const max = value.max ? endOfDay(value.max).toISOString() : undefined;

  if (!min && !max) {
    return undefined;
  }

  return {
    ...(min && { min }),
    ...(max && { max }),
  };
};

const normalizeBooleanSelection = (
  value: PipelineDealsAppliedFilters['isComplete'],
): boolean | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const normalizeStageFilter = (
  value: PipelineDealsAppliedFilters['stage'],
): PipelineDealsApiFilter['stage'] | undefined => {
  const ids = normalizeIds(value.id);
  const pipelineId = isFilledText(value.pipelineId)
    ? value.pipelineId.trim()
    : undefined;
  const name = isFilledText(value.name) ? value.name.trim() : undefined;

  if (!ids.length && !pipelineId && !name) {
    return undefined;
  }

  return {
    ...(ids.length > 0 && { id: ids }),
    ...(pipelineId && { pipelineId }),
    ...(name && { name }),
  };
};

const normalizeProductFilter = (
  value: PipelineDealsAppliedFilters['product'],
): PipelineDealsApiFilter['product'] | undefined => {
  const filter = isFilledText(value.name) ? value.name.trim() : undefined;
  const normalized = {
    ...(filter && { filter }),
    ...(value.bankPartnerId && {
      financialInstitutionId: value.bankPartnerId,
    }),
    ...(value.table && { table: value.table }),
    ...(value.type && { type: value.type }),
    ...(value.segment && { segment: value.segment }),
  };

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

const normalizeStageSemaphoreFlags = (
  values: PipelineDealsAppliedFilters['stageSemaphoreFlag'],
): StageSemaphoreFlagType[] => {
  return [...new Set(values)]
    .filter((value) => value !== undefined)
    .sort((left, right) => left - right) as StageSemaphoreFlagType[];
};

const resolveFilterMode = (
  filters: PipelineDealsAppliedFilters,
  options: SerializeFiltersOptions,
): UserPermission => {
  const selectedMode = filters.owner ?? options.defaultMode;
  const hasScopeOrSearchOverride =
    (isFilledText(filters.search) ||
      filters.companyId.length > 0 ||
      filters.subsidiaryId.length > 0) &&
    options.canViewAll;

  return hasScopeOrSearchOverride ? 'all' : selectedMode;
};

const sortFilterValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortFilterValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort((left, right) => left.localeCompare(right))
      .reduce<Record<string, unknown>>((acc, key) => {
        const nestedValue = sortFilterValue(
          (value as Record<string, unknown>)[key],
        );

        if (nestedValue !== undefined) {
          acc[key] = nestedValue;
        }

        return acc;
      }, {});
  }

  return value;
};

const buildStableFilterKey = (payload: {
  filter?: PipelineDealsApiFilter;
  options: {
    mode: UserPermission;
  };
}): string => {
  return JSON.stringify(sortFilterValue(payload));
};

export const createDefaultPipelineDealsFilters = (
  defaultMode: UserPermission,
): PipelineDealsAppliedFilters => ({
  search: '',
  owner: defaultMode,
  companyId: [],
  subsidiaryId: [],
  segment: undefined,
  origin: [],
  createdAt: {},
  leadScore: {},
  totalIncome: {},
  operationValue: {},
  approvedValue: {},
  asset: {
    type: undefined,
    value: {},
  },
  stage: {
    id: [],
    pipelineId: undefined,
    name: undefined,
  },
  user: {
    id: [],
  },
  product: {
    name: '',
    table: undefined,
    type: undefined,
    bankPartnerId: undefined,
    segment: undefined,
  },
  isComplete: '',
  isFavorite: '',
  operationPotential: undefined,
  stageSemaphoreFlag: [],
  timeToBuy: [],
});

export const clonePipelineDealsFilters = (
  filters: PipelineDealsAppliedFilters,
): PipelineDealsAppliedFilters => ({
  search: filters.search,
  owner: filters.owner,
  companyId: [...filters.companyId],
  subsidiaryId: [...filters.subsidiaryId],
  ...(filters.segment && { segment: filters.segment }),
  origin: [...filters.origin],
  createdAt: {
    ...(filters.createdAt.min && { min: new Date(filters.createdAt.min) }),
    ...(filters.createdAt.max && { max: new Date(filters.createdAt.max) }),
  },
  leadScore: cloneTextRange(filters.leadScore),
  totalIncome: cloneTextRange(filters.totalIncome),
  operationValue: cloneTextRange(filters.operationValue),
  approvedValue: cloneTextRange(filters.approvedValue),
  asset: {
    ...(filters.asset.type && { type: filters.asset.type }),
    value: cloneTextRange(filters.asset.value),
  },
  stage: {
    id: [...filters.stage.id],
    ...(filters.stage.pipelineId && { pipelineId: filters.stage.pipelineId }),
    ...(filters.stage.name && { name: filters.stage.name }),
  },
  user: {
    id: [...filters.user.id],
  },
  product: {
    name: filters.product.name,
    ...(filters.product.table && { table: filters.product.table }),
    ...(filters.product.type && { type: filters.product.type }),
    ...(filters.product.bankPartnerId && {
      bankPartnerId: filters.product.bankPartnerId,
    }),
    ...(filters.product.segment && { segment: filters.product.segment }),
  },
  isComplete: filters.isComplete,
  isFavorite: filters.isFavorite,
  ...(filters.operationPotential && {
    operationPotential: filters.operationPotential,
  }),
  stageSemaphoreFlag: [...filters.stageSemaphoreFlag],
  timeToBuy: [...filters.timeToBuy],
});

export const countActivePipelineDealsFilters = (
  filters: PipelineDealsAppliedFilters,
  options?: {
    defaultMode?: UserPermission;
  },
): number => {
  const hasActiveOwnerFilter =
    options?.defaultMode !== undefined
      ? filters.owner !== options.defaultMode
      : false;

  return [
    hasActiveOwnerFilter,
    isFilledText(filters.search),
    hasValidArrayItem(filters.companyId, isFilledText),
    hasValidArrayItem(filters.subsidiaryId, isFilledText),
    isFilledText(filters.segment),
    hasValidArrayItem(filters.origin, isFilledText),
    hasFilledDateRange(filters.createdAt),
    hasFilledTextRange(filters.leadScore),
    hasFilledTextRange(filters.totalIncome),
    hasFilledTextRange(filters.operationValue),
    hasFilledTextRange(filters.approvedValue),
    isFilledText(filters.asset.type),
    hasFilledTextRange(filters.asset.value),
    hasActiveStageFilter(filters.stage),
    hasValidArrayItem(filters.user.id, isFilledText),
    isFilledText(filters.product.name),
    isFilledText(filters.product.table),
    isFilledText(filters.product.type),
    isFilledText(filters.product.bankPartnerId),
    isFilledText(filters.product.segment),
    hasSelectedBooleanValue(filters.isComplete),
    hasSelectedBooleanValue(filters.isFavorite),
    isFilledText(filters.operationPotential),
    hasValidArrayItem(filters.stageSemaphoreFlag, (value) =>
      Number.isFinite(value),
    ),
    hasValidArrayItem(filters.timeToBuy, isFilledText),
  ].filter(Boolean).length;
};

export const serializePipelineDealsFilters = (
  filters: PipelineDealsAppliedFilters,
  options: SerializeFiltersOptions,
): PipelineDealsSerializedFilters => {
  const search = isFilledText(filters.search)
    ? filters.search.trim()
    : undefined;
  const companyId = normalizeIds(filters.companyId);
  const subsidiaryId = normalizeIds(filters.subsidiaryId);
  const origin = [...new Set(filters.origin)].sort((left, right) =>
    left.localeCompare(right),
  );
  const userIds = normalizeIds(filters.user.id);
  const operationValue = toOptionalCurrencyRange(
    filters.operationValue.min,
    filters.operationValue.max,
  );
  const approvedValue = toOptionalCurrencyRange(
    filters.approvedValue.min,
    filters.approvedValue.max,
  );
  const totalIncome = toOptionalIntegerCurrencyRange(
    filters.totalIncome.min,
    filters.totalIncome.max,
  );
  const assetValue = toOptionalCurrencyRange(
    filters.asset.value.min,
    filters.asset.value.max,
  );
  const leadScore = toOptionalNumberRange(
    filters.leadScore.min,
    filters.leadScore.max,
  );
  const createdAt = normalizeCreatedAtRange(filters.createdAt);
  const stage = normalizeStageFilter(filters.stage);
  const product = normalizeProductFilter(filters.product);
  const isComplete = normalizeBooleanSelection(filters.isComplete);
  const isFavorite = normalizeBooleanSelection(filters.isFavorite);
  const stageSemaphoreFlag = normalizeStageSemaphoreFlags(
    filters.stageSemaphoreFlag,
  );
  const timeToBuy = normalizeTimeToBuy(filters.timeToBuy);
  const filter: PipelineDealsApiFilter = {
    ...(search && { search }),
    ...(companyId.length > 0 && { companyId }),
    ...(subsidiaryId.length > 0 && { subsidiaryId }),
    ...(filters.segment && { segment: filters.segment }),
    ...(origin.length > 0 && { origin }),
    ...(isFavorite !== undefined && { isFavorite }),
    ...(filters.operationPotential && {
      operationPotential: filters.operationPotential,
    }),
    ...(userIds.length > 0 && {
      user: {
        id: userIds,
      },
    }),
    ...(operationValue && { operationValue }),
    ...(approvedValue && { approvedValue }),
    ...(totalIncome && { totalIncome }),
    ...((filters.asset.type || assetValue) && {
      asset: {
        ...(filters.asset.type && { type: filters.asset.type }),
        value: assetValue ?? {},
      },
    }),
    ...(leadScore && { leadScore }),
    ...(createdAt && { createdAt }),
    ...(stage && { stage }),
    ...(product && { product }),
    ...(isComplete !== undefined && { isComplete }),
    ...(stageSemaphoreFlag.length > 0 && {
      stageSemaphoreFlag,
    }),
    ...(timeToBuy.length > 0 && { timeToBuy }),
  };
  const mode = resolveFilterMode(filters, options);
  const serializedPayload = {
    ...(Object.keys(filter).length > 0 && { filter }),
    options: {
      mode,
    },
  } satisfies {
    filter?: PipelineDealsApiFilter;
    options: {
      mode: UserPermission;
    };
  };

  return {
    ...serializedPayload,
    filterKey: buildStableFilterKey(serializedPayload),
    mode,
  };
};
